/**
 * 主从报表区域标注与左父格补全（复杂布局 / AI 手写 IR 路径的兜底）。
 *
 * 背景：`buildMasterDetail` 只能生成「两层、单从表、down、列从 col=1 对齐」的固定
 * 6 行模板；当布局超出该模板（多从表、列不对齐、表头位置特殊等）时，AI 只能手写 IR，
 * 此时左父格（left）关系容易漏写。本模块让 AI 在生成阶段只给出「语义标注」（哪个是主
 * 数据集、分组字段、从数据集、关联字段、以及从表各行的行号），坐标与 left 由代码确定
 * 性地推导并补全。
 *
 * 职责边界（关键）：
 *   - LLM 负责「语义识别」：主/从数据集、关联字段、表头/标题/数据行号；
 *   - 代码负责「几何计算」：定位顶层左父格、判断某格是否在锚点列左侧、补 left/top。
 *   LLM 绝不直接输出 left="A2" 这类坐标字符串。
 */

import type { CellIR, ReportIR } from '../ir/types.js';
import { coordName } from './coordinate.js';
import { findTopParentCells, normalizeTopParentCells } from './parent.js';

/** 主从报表区域标注（生成辅助信息，独立于 ReportIR，不参与序列化）。 */
export interface MasterDetailAnnotation {
  /** 主表（父数据集）信息。 */
  master: {
    /** 主数据集名（决定父表数据区归属）。 */
    dataset: string;
    /** 主表分组字段（决定顶层左父格锚点所在的数据列）。 */
    groupField: string;
  };
  /** 从表（子数据集）列表，支持多从表。 */
  details: Array<{
    /** 从数据集名。 */
    dataset: string;
    /** 子表关联字段（conditions.left）。 */
    joinField: string;
    /** 关联到主表字段（conditions.field）。 */
    masterField: string;
    /** 从表数据所在行（必有）。 */
    dataRow: number;
    /** 从表表头所在行（可选）。 */
    headerRow?: number;
    /** 从表标题所在行（可选）。 */
    titleRow?: number;
  }>;
}

/** 补全结果中的警告项（不抛错，供调用方呈现/提示 AI 重写）。 */
export interface RegionWarning {
  cell?: string;
  message: string;
}

/** 应用补全后的结果：单元格数组 + 警告列表。 */
export interface ApplyMasterDetailResult {
  cells: CellIR[];
  warnings: RegionWarning[];
}

/**
 * 依据区域标注，为「AI 手写 IR」自动补上左父格（left）关系，达到与 buildMasterDetail
 * 一致的父子格链。规则遵循 src/references/top-parent-cell.md：
 *
 *   1. 顶层左父格自身 left='none'（交给 normalizeTopParentCells 兜底，若 row>1 再断 top）；
 *   2. 顶层左父格所在「锚点列」以左的每一列，以及主表数据区其余列（col>锚点列），
 *      其 left 一律指向顶层左父格坐标；
 *   3. 每个从表：dataRow / headerRow / titleRow 行的第一列（及锚点列左侧的列）left
 *      指向顶层左父格；从表其余列（col>锚点列）保持 default（不写显式 left）。
 *
 * 显式优先：cell.left 已存在且非 'default' 时不覆盖。
 */
export function applyMasterDetailParents(
  report: ReportIR,
  annotation: MasterDetailAnnotation,
): ApplyMasterDetailResult {
  const warnings: RegionWarning[] = [];
  const cells = report.cells;

  // 1. 定位顶层左父格（down 方向扩展树中 row 最小、并列取 col 最小者）。
  const { left: topLeft } = findTopParentCells(cells);
  if (!topLeft) {
    // 没有任何 expand='down' 的格子，无法确定锚点：原样返回（交 normalize 与校验兜底）。
    warnings.push({ message: '未找到 expand=down 的扩展格，无法定位顶层左父格，跳过 left 补全。' });
    return { cells, warnings };
  }
  const anchorCol = topLeft.col;
  const anchorName = coordName(topLeft.row, topLeft.col);

  // 主表数据集名，用于校验（可选，缺失不阻断）。
  const masterDataset = annotation.master.dataset;

  // 2. 顶层左父格自身 left='none'：由 normalizeTopParentCells 统一兜底，这里不重复写。

  // 3. 补全：遍历 cells，按规则为「锚点列左侧」及「主表数据区其余列」补 left。
  const result: CellIR[] = cells.map((cell) => {
    // 显式优先：已有非 default 的 left，不覆盖。
    if (cell.left && cell.left !== 'default') return cell;

    const isDetailRow = annotation.details.some(
      (d) => cell.row === d.dataRow || cell.row === d.headerRow || cell.row === d.titleRow,
    );

    if (cell.col <= anchorCol) {
      // 锚点列及其左侧的所有单元格：left 指向顶层左父格。
      // （主表表头、主表数据其余列、从表标题/表头/数据/占位第一列均在此覆盖。）
      return { ...cell, left: anchorName };
    }

    // col > anchorCol：仅主表数据区其余列需要指向锚点，从表其余列保持 default。
    // 主表数据区 = 属于主数据集且 expand='down' 的格子；从表数据格带 conditions。
    const isMasterData =
      cell.expand === 'down' &&
      cell.content?.kind === 'dataset' &&
      cell.content.dataset === masterDataset &&
      !cell.content.conditions?.length;
    if (isMasterData) {
      return { ...cell, left: anchorName };
    }

    return cell;
  });

  // 4. 校验兜底：产生 warning 而非抛错。
  for (const detail of annotation.details) {
    // dataRow 行必须存在属于该从数据集、且带 conditions 的 dataset 格。
    const dataCells = cells.filter((c) => c.row === detail.dataRow);
    if (dataCells.length === 0) {
      warnings.push({ message: `从表 "${detail.dataset}" 的 dataRow=${detail.dataRow} 行不存在任何单元格。` });
      continue;
    }
    const hasDetailData = dataCells.some(
      (c) =>
        c.content?.kind === 'dataset' &&
        c.content.dataset === detail.dataset &&
        (c.content.conditions?.length ?? 0) > 0,
    );
    if (!hasDetailData) {
      warnings.push({
        message: `从表 "${detail.dataset}" 的 dataRow=${detail.dataRow} 行未找到带 conditions 的 dataset 格，行号可能标注有误。`,
      });
    }
    // headerRow / titleRow 行应是 text 格（若有）。
    for (const [row, kind] of [
      [detail.headerRow, 'headerRow'],
      [detail.titleRow, 'titleRow'],
    ] as Array<[number | undefined, string]>) {
      if (row == null) continue;
      if (row < 1 || row > report.rowCount) {
        warnings.push({ message: `从表 "${detail.dataset}" 的 ${kind}=${row} 越界（1..${report.rowCount}）。` });
        continue;
      }
      const rowCells = cells.filter((c) => c.row === row);
      if (rowCells.length && !rowCells.some((c) => c.content?.kind === 'text')) {
        warnings.push({ message: `从表 "${detail.dataset}" 的 ${kind}=${row} 行未找到 text 表头格。` });
      }
    }
    // 关联字段一致性：joinField 应等于 dataRow 行 dataset 格的 conditions.left。
    const firstDetail = dataCells.find(
      (c) => c.content?.kind === 'dataset' && c.content.dataset === detail.dataset && (c.content.conditions?.length ?? 0) > 0,
    );
    if (firstDetail && firstDetail.content?.kind === 'dataset') {
      const cond = firstDetail.content.conditions?.[0];
      if (cond && cond.valueType === 'dataset') {
        if (cond.left !== detail.joinField) {
          warnings.push({ message: `从表 "${detail.dataset}" 的 joinField=${detail.joinField} 与 cells 中 conditions.left=${cond.left} 不一致。` });
        }
        if (cond.dataset !== masterDataset) {
          warnings.push({ message: `从表 "${detail.dataset}" 的 conditions.dataset=${cond.dataset} 与主数据集 ${masterDataset} 不一致。` });
        }
        if (cond.field !== annotation.master.groupField) {
          warnings.push({ message: `从表 "${detail.dataset}" 的 conditions.field=${cond.field} 与 master.groupField=${annotation.master.groupField} 不一致。` });
        }
      }
    }
  }

  // 5. 最后统一跑一遍顶层父格规范化（钉 left='none'、断开交叉环）。
  return { cells: normalizeTopParentCells(result), warnings };
}

/**
 * 从 IR 顶层旁路字段 `masterDetailAnnotation` 中读取标注，并返回剥离后的干净 IR。
 * 用于方案 Y：AI 产出单文件 JSON，序列化前消费并剥离，不污染正式 schema 与 XML。
 *
 * 注意：该字段不写入 schema.json（顶层 additionalProperties=false 会拦截），
 * 因此必须在 validateIR 之前调用本函数剥离。
 */
export function extractMasterDetailAnnotation(
  ir: ReportIR & { masterDetailAnnotation?: MasterDetailAnnotation },
): { report: ReportIR; annotation: MasterDetailAnnotation | null } {
  const { masterDetailAnnotation, ...report } = ir;
  return { report, annotation: masterDetailAnnotation ?? null };
}
